import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { fmt } from "@/hooks/useRates";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METALS = ["ALUMINIUM", "COPPER", "GOLD", "SILVER", "ZINC", "LEAD", "NICKEL"];
const COLORS = { COPPER: "#B87333", ALUMINIUM: "#9BA8B5" };

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-[#0A0A0A] border border-[#25D366]/40 text-white px-4 py-3 font-ticker text-xs space-y-1">
      <p className="text-white/50">{new Date(p.t).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
      <p className="font-semibold text-[#25D366]">₹{fmt(p.mcx, 2)}</p>
      <p className="text-white/70">${fmt(p.lme, 2)}</p>
    </div>
  );
};

export default function ChartsSection() {
  const { str } = useLang();
  const [symbol, setSymbol] = useState("ALUMINIUM");
  const [range, setRange] = useState("daily");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/history/${symbol}?range=${range}`)
      .then((res) => mounted && setData(res.data))
      .catch(() => mounted && setData(null))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [symbol, range]);

  const color = COLORS[symbol] || "#25D366";
  const points = data?.points || [];

  return (
    <section
      id="charts"
      data-testid="charts-section"
      className="relative px-6 sm:px-10 lg:px-16 py-24 lg:py-32 border-b border-white/10 noise"
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
        <div>
          <p className="font-ticker text-[11px] tracking-[0.4em] uppercase text-[#25D366] mb-4">
            {str.chartsTag}
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none">
            {str.chartsTitle1} <span className="text-stroke-gold">{str.chartsTitle2}</span>
          </h2>
        </div>
        <div className="flex gap-2" data-testid="chart-range-toggle">
          {["daily", "weekly"].map((r) => (
            <button
              key={r}
              data-testid={`chart-range-${r}`}
              onClick={() => setRange(r)}
              className={`px-5 py-2.5 font-ticker text-[11px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                range === r ? "bg-[#25D366] text-[#0A0A0A] font-semibold" : "bg-white/[0.06] text-white/60 hover:text-white"
              }`}
            >
              {r === "daily" ? str.day : str.week}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="chart-metal-tabs">
        {METALS.map((m) => (
          <button
            key={m}
            data-testid={`chart-tab-${m.toLowerCase()}`}
            onClick={() => setSymbol(m)}
            className={`px-4 py-2 font-ticker text-[10px] tracking-[0.2em] uppercase border transition-colors duration-300 ${
              symbol === m
                ? "bg-[#25D366] border-[#25D366] text-[#0A0A0A] font-semibold"
                : "border-white/15 text-white/55 hover:border-[#25D366] hover:text-[#25D366]"
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="border border-white/10 bg-[#121212]/60 p-4 sm:p-8 relative z-10"
        data-testid="chart-container"
      >
        {loading ? (
          <div className="h-[360px] flex items-center justify-center font-ticker text-xs tracking-[0.25em] uppercase text-white/40">
            {str.chartLoading}
          </div>
        ) : points.length === 0 ? (
          <div className="h-[360px] flex items-center justify-center font-ticker text-xs tracking-[0.25em] uppercase text-white/40">
            {str.chartNoData}
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
              <p className="font-display font-extrabold uppercase tracking-tight text-xl">
                {data.name} <span className="text-white/40 font-ticker text-xs normal-case">({data.mcx_unit})</span>
              </p>
              <p className="font-ticker text-sm font-semibold text-[#25D366]">
                ₹{fmt(points[points.length - 1].mcx, 2)} · ${fmt(points[points.length - 1].lme, 2)}
              </p>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <AreaChart data={points} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis
                  dataKey="t"
                  tickFormatter={(t) =>
                    range === "daily"
                      ? new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                      : new Date(t).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
                  }
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "rgba(255,255,255,0.4)" }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={40}
                />
                <YAxis
                  domain={["auto", "auto"]}
                  tickFormatter={(v) => fmt(v, 0)}
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "rgba(255,255,255,0.4)" }}
                  axisLine={false}
                  tickLine={false}
                  width={72}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="mcx" stroke={color} strokeWidth={2} fill="url(#chartFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </>
        )}
      </motion.div>
    </section>
  );
}
